import React, {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from 'react';

import Button from '@/components/Button';
import Card from '@/components/Card';
import Chip from '@/components/Chip';
import EmptyState from '@/components/EmptyState';
import LoadingState from '@/components/LoadingState';
import Modal from '@/components/Modal';
import Page from '@/components/Page';
import Skeleton from '@/components/Skeleton';
import Stack from '@/components/Stack';
import Table from '@/components/Table';
import TableHeader from '@/components/TableHeader';
import { Body, Heading, Small } from '@/components/Typography';
import { TableCell, TableRow } from '@/elements/Table';
import Link from '@/elements/Link';
import ROUTES from '@/routes/routes';

import {
  useAddLineItem,
  useCreateInvoice,
  useCycleAccounts,
  useCycles,
  useInvoiceDetail,
  useReconcile,
  useVoidInvoice,
} from './hooks/useInvoiceHealth';
import type {
  ReconcileAction,
  ReconcilePreview,
} from './hooks/useInvoiceHealth';
import type {
  CycleAccount,
  CycleTimesheet,
  DeepInvoiceDetail,
  InvoiceCycle,
} from './types';

const humanizeStatus = (status: string) =>
  status.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());

const ONE_WEEK_IN_DAYS = 7;

const formatDateTime = (value: string | null) => {
  if (!value) return 'n/a';

  return new Date(value).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatCurrency = (amountCents: number | null) => {
  if (amountCents == null) return 'n/a';

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amountCents / 100);
};

const statusColor = (
  status: string,
): 'success' | 'warning' | 'error' | 'default' => {
  if (status === 'included') return 'success';
  if (status === 'not_included') return 'warning';
  if (status === 'linked_to_other_invoice' || status === 'requires_review') {
    return 'error';
  }

  return 'default';
};

const shiftIsoDate = (value: string | null, days: number) => {
  if (!value) return null;

  const shifted = new Date(value);
  shifted.setDate(shifted.getDate() + days);
  return shifted.toISOString();
};

const formatCycleDateLabel = (value: string) =>
  new Date(value).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

const formatCycleDayLabel = (value: string) =>
  new Date(value).toLocaleDateString('en-US', {
    weekday: 'long',
  });

const buildPreviewCycle = (cycle: InvoiceCycle): InvoiceCycle => {
  const startedAt = shiftIsoDate(cycle.started_at, ONE_WEEK_IN_DAYS)!;
  const endedAt = shiftIsoDate(cycle.ended_at, ONE_WEEK_IN_DAYS);
  const from = shiftIsoDate(cycle.from, ONE_WEEK_IN_DAYS)!;
  const to = shiftIsoDate(cycle.to, ONE_WEEK_IN_DAYS)!;

  return {
    ...cycle,
    id: cycle.id * -1,
    status: 'preview',
    started_at: startedAt,
    ended_at: endedAt,
    date_label: formatCycleDateLabel(startedAt),
    day_label: formatCycleDayLabel(startedAt),
    invoice_count: 0,
    timesheet_count: 0,
    failed_account_count: 0,
    from,
    to,
    is_preview: true,
    preview_base_cycle_id: cycle.id,
    preview_base_label: `${cycle.date_label} (${cycle.day_label})`,
  };
};

type AccountFilter = 'all' | 'complete' | 'incomplete' | 'no_invoice';

type CycleDropdownProps = {
  cycles: InvoiceCycle[];
  selectedCycle: InvoiceCycle | null;
  onSelect: (cycle: InvoiceCycle) => void;
};

const CycleDropdown: React.FC<CycleDropdownProps> = ({
  cycles,
  selectedCycle,
  onSelect,
}) => {
  const selected = selectedCycle;
  const previewBaseCycle =
    selected?.is_preview && selected.preview_base_cycle_id != null
      ? cycles.find((cycle) => cycle.id === selected.preview_base_cycle_id) ||
        cycles[0] ||
        null
      : selected && !selected.is_preview
        ? selected
        : cycles[0] || null;
  const previewCycle = previewBaseCycle
    ? buildPreviewCycle(previewBaseCycle)
    : null;
  const selectableCycles = previewCycle
    ? [
        previewCycle,
        ...cycles,
        ...(selected?.is_preview && selected.id !== previewCycle.id
          ? [selected]
          : []),
      ]
    : cycles;

  return (
    <Stack vertical gap={8}>
      <div style={{ minWidth: 320 }}>
        <select
          value={selected?.id ?? ''}
          onChange={(event) => {
            const cycle = selectableCycles.find(
              (entry) => entry.id === Number(event.target.value),
            );
            if (cycle) onSelect(cycle);
          }}
          style={{
            width: '100%',
            padding: '10px 14px',
            borderRadius: 8,
            border: '1px solid #d0d5dd',
            background: '#fff',
            fontSize: 14,
            fontWeight: 500,
            color: '#344054',
            cursor: 'pointer',
            appearance: 'none',
          }}
        >
          <option value="" disabled>
            Select an invoice cycle...
          </option>
          {selectableCycles.map((cycle) => (
            <option key={cycle.id} value={cycle.id}>
              {cycle.is_preview ? 'Future: ' : ''}
              {cycle.date_label} ({cycle.day_label})
            </option>
          ))}
        </select>
      </div>

      {selected && (
        <Stack gap={8} align="center" wrap>
          <Chip
            color="default"
            label={selected.is_preview ? 'preview' : selected.status}
            size="small"
          />
          {selected.is_preview ? (
            <Small>
              Previewing next week&apos;s run based on{' '}
              {selected.preview_base_label || 'the selected cycle'}
            </Small>
          ) : (
            <>
              <Small>
                {selected.invoice_count} invoices and {selected.timesheet_count}{' '}
                timesheets in cycle
              </Small>
              {selected.failed_account_count > 0 && (
                <div style={{ color: '#b42318', fontSize: 12 }}>
                  {selected.failed_account_count} failed accounts
                </div>
              )}
            </>
          )}
        </Stack>
      )}
    </Stack>
  );
};

type ConfirmState = {
  accountName: string;
  timesheet: CycleTimesheet;
} | null;

type ConfirmModalProps = {
  confirmState: ConfirmState;
  loading: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
};

const ConfirmSyncModal: React.FC<ConfirmModalProps> = ({
  confirmState,
  loading,
  error,
  onCancel,
  onConfirm,
}) => {
  if (!confirmState) return null;

  return (
    <Modal
      displayFlex
      size="xs"
      title="Add line item"
      onRequestClose={loading ? undefined : onCancel}
      wrapperBackground
    >
      <Card>
        <Card.Section>
          <Stack vertical gap={16}>
            <Body weight="semibold">
              Add timesheet {confirmState.timesheet.id} to the invoice for{' '}
              {confirmState.accountName}?
            </Body>
            <Small>
              If the account does not have an invoice yet for this cycle, a new
              invoice will be created first and the line item will be synced
              immediately.
            </Small>
            {error && (
              <div style={{ color: '#b42318', fontSize: 14 }}>{error}</div>
            )}
            <Stack justify="end" gap={12}>
              <Button
                a11yLabel="Cancel"
                appearance="outline"
                label="Cancel"
                disabled={loading}
                type="button"
                onClick={onCancel}
              />
              <Button
                a11yLabel="Confirm add line item"
                label="Add line item"
                isLoading={loading}
                type="button"
                onClick={onConfirm}
              />
            </Stack>
          </Stack>
        </Card.Section>
      </Card>
    </Modal>
  );
};

type ConfirmCreateState = {
  accountName: string;
  accountId: number;
} | null;

type ConfirmCreateModalProps = {
  state: ConfirmCreateState;
  loading: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
};

const ConfirmCreateInvoiceModal: React.FC<ConfirmCreateModalProps> = ({
  state,
  loading,
  error,
  onCancel,
  onConfirm,
}) => {
  if (!state) return null;

  return (
    <Modal
      displayFlex
      size="xs"
      title="Create invoice"
      onRequestClose={loading ? undefined : onCancel}
      wrapperBackground
    >
      <Card>
        <Card.Section>
          <Stack vertical gap={16}>
            <Body weight="semibold">
              Create a new invoice for {state.accountName} (Account{' '}
              {state.accountId})?
            </Body>
            <Small>
              This will create an empty invoice for the selected cycle. You can
              then add line items individually.
            </Small>
            {error && (
              <div style={{ color: '#b42318', fontSize: 14 }}>{error}</div>
            )}
            <Stack justify="end" gap={12}>
              <Button
                a11yLabel="Cancel"
                appearance="outline"
                label="Cancel"
                disabled={loading}
                type="button"
                onClick={onCancel}
              />
              <Button
                a11yLabel="Confirm create invoice"
                label="Create invoice"
                isLoading={loading}
                type="button"
                onClick={onConfirm}
              />
            </Stack>
          </Stack>
        </Card.Section>
      </Card>
    </Modal>
  );
};

type ConfirmVoidState = {
  accountName: string;
  invoiceId: number;
} | null;

type ConfirmVoidModalProps = {
  state: ConfirmVoidState;
  loading: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
};

const ConfirmVoidInvoiceModal: React.FC<ConfirmVoidModalProps> = ({
  state,
  loading,
  error,
  onCancel,
  onConfirm,
}) => {
  if (!state) return null;

  return (
    <Modal
      displayFlex
      size="xs"
      title="Void invoice"
      onRequestClose={loading ? undefined : onCancel}
      wrapperBackground
    >
      <Card>
        <Card.Section>
          <Stack vertical gap={16}>
            <Body weight="semibold">
              Void invoice #{state.invoiceId} for {state.accountName}?
            </Body>
            <Small>
              This will void the invoice in Stripe, reset all linked timesheets
              so they can be re-invoiced, and trigger a new invoice to be
              created.
            </Small>
            {error && (
              <div style={{ color: '#b42318', fontSize: 14 }}>{error}</div>
            )}
            <Stack justify="end" gap={12}>
              <Button
                a11yLabel="Cancel"
                appearance="outline"
                label="Cancel"
                disabled={loading}
                type="button"
                onClick={onCancel}
              />
              <Button
                a11yLabel="Confirm void invoice"
                appearance="outline"
                label="Void invoice"
                isLoading={loading}
                type="button"
                onClick={onConfirm}
              />
            </Stack>
          </Stack>
        </Card.Section>
      </Card>
    </Modal>
  );
};

const ACTION_LABELS: Record<string, string> = {
  void_duplicate: 'Void duplicate invoice',
  fix_totals: 'Sync totals from Stripe',
  items_missing_locally: 'Create missing local items',
  items_orphaned_locally: 'Remove orphaned local items',
  fix_item_amount: 'Fix item amount mismatch',
  stripe_invoice_not_found: 'Stripe invoice not found',
  error: 'Error during reconciliation',
};

const actionLabel = (action: string) =>
  ACTION_LABELS[action] || action.replace(/_/g, ' ');

type ReconcileModalProps = {
  isOpen: boolean;
  accountName: string | null;
  accountId: number | null;
  preview: ReconcilePreview | null;
  loading: boolean;
  executing: boolean;
  error: string | null;
  onClose: () => void;
  onExecute: () => void;
};

const ReconcileModal: React.FC<ReconcileModalProps> = ({
  isOpen,
  accountName,
  accountId,
  preview,
  loading,
  executing,
  error,
  onClose,
  onExecute,
}) => {
  if (!isOpen) return null;

  const actions = preview?.actions || [];
  const hasActions = actions.length > 0;
  const isExecuted = preview?.mode === 'execute';

  return (
    <Modal
      size="lg"
      title={`Reconcile ${accountName || `Account ${accountId}`}`}
      onRequestClose={executing ? undefined : onClose}
    >
      <div style={{ padding: '16px 0' }}>
        {loading && <LoadingState text="Scanning invoices against Stripe..." />}
        {!loading && error && (
          <div style={{ color: '#b42318', fontSize: 14 }}>{error}</div>
        )}
        {!loading && preview && (
          <Stack vertical gap={16}>
            <Card>
              <Card.Section>
                <Stack vertical gap={6}>
                  <Small>Invoices scanned: {preview.invoices_scanned}</Small>
                  {Object.entries(preview.stats).map(([key, value]) =>
                    value > 0 ? (
                      <Small key={key}>
                        {key.replace(/_/g, ' ')}: {value}
                      </Small>
                    ) : null,
                  )}
                </Stack>
              </Card.Section>
            </Card>

            {!hasActions && (
              <Card>
                <Card.Section>
                  <EmptyState title="No reconciliation actions needed — everything is in sync" />
                </Card.Section>
              </Card>
            )}

            {hasActions && (
              <Card>
                <Card.Section>
                  <Body weight="semibold">
                    {isExecuted ? 'Actions executed' : 'Proposed actions'} (
                    {actions.length})
                  </Body>
                </Card.Section>
                <Card.Section>
                  <Stack vertical gap={8}>
                    {actions.map((action: ReconcileAction, idx: number) => (
                      <Stack key={idx} vertical gap={2}>
                        <Stack gap={8} align="center">
                          <Chip
                            color={
                              isExecuted
                                ? 'success'
                                : action.action === 'error'
                                  ? 'error'
                                  : 'warning'
                            }
                            label={actionLabel(action.action)}
                            size="small"
                          />
                          <Small>Invoice #{action.invoice_id}</Small>
                          {action.stripe_invoice_id && (
                            <Small>({action.stripe_invoice_id})</Small>
                          )}
                        </Stack>
                        <Small>
                          {typeof action.details === 'object'
                            ? Object.entries(action.details)
                                .map(
                                  ([k, v]) =>
                                    `${k.replace(/_/g, ' ')}: ${typeof v === 'object' ? JSON.stringify(v) : v}`,
                                )
                                .join(', ')
                            : String(action.details)}
                        </Small>
                      </Stack>
                    ))}
                  </Stack>
                </Card.Section>
              </Card>
            )}

            <Stack justify="end" gap={12}>
              <Button
                a11yLabel="Close"
                appearance="outline"
                label={isExecuted ? 'Done' : 'Cancel'}
                disabled={executing}
                type="button"
                onClick={onClose}
              />
              {hasActions && !isExecuted && (
                <Button
                  a11yLabel="Execute reconciliation"
                  label="Execute reconciliation"
                  isLoading={executing}
                  type="button"
                  onClick={onExecute}
                />
              )}
            </Stack>
          </Stack>
        )}
      </div>
    </Modal>
  );
};

type InvoiceStripeModalProps = {
  isOpen: boolean;
  loading: boolean;
  error: string | null;
  invoiceDetail: DeepInvoiceDetail | null;
  onClose: () => void;
};

const InvoiceStripeModal: React.FC<InvoiceStripeModalProps> = ({
  isOpen,
  loading,
  error,
  invoiceDetail,
  onClose,
}) => {
  if (!isOpen) return null;

  return (
    <Modal size="lg" title="Invoice details" onRequestClose={onClose}>
      <div style={{ padding: '16px 0' }}>
        {loading && <LoadingState text="Loading invoice details..." />}
        {!loading && error && (
          <div style={{ color: '#b42318', fontSize: 14 }}>{error}</div>
        )}
        {!loading && invoiceDetail && (
          <Stack vertical gap={16}>
            <Stack vertical gap={6}>
              <Heading>Invoice {invoiceDetail.invoice.id}</Heading>
              <Small>
                Stripe ID: {invoiceDetail.invoice.stripe_invoice_id || 'n/a'}
              </Small>
              <Chip color="default" label={invoiceDetail.status} size="small" />
            </Stack>

            {invoiceDetail.issues.length > 0 && (
              <Stack gap={8} wrap>
                {invoiceDetail.issues.map((issue) => (
                  <Chip
                    key={issue}
                    color="warning"
                    label={humanizeStatus(issue)}
                    size="small"
                  />
                ))}
              </Stack>
            )}

            <Card>
              <Card.Section>
                <Stack vertical gap={8}>
                  <Small>
                    Local invoice items:{' '}
                    {invoiceDetail.local_invoice_items.length}
                  </Small>
                  <Small>
                    Stripe invoice items:{' '}
                    {invoiceDetail.stripe_invoice_items.length}
                  </Small>
                  <Small>
                    Missing locally:{' '}
                    {invoiceDetail.stripe_items_missing_locally.length}
                  </Small>
                  <Small>
                    Missing in Stripe:{' '}
                    {invoiceDetail.local_items_missing_in_stripe.length}
                  </Small>
                </Stack>
              </Card.Section>
            </Card>

            {invoiceDetail.local_invoice_items.length > 0 && (
              <Card>
                <Card.Section>
                  <Body weight="semibold">Local invoice items</Body>
                </Card.Section>
                <Card.Section>
                  <Stack vertical gap={6}>
                    {invoiceDetail.local_invoice_items
                      .slice(0, 15)
                      .map((item) => (
                        <Small key={item.id}>
                          #{item.id} timesheet {item.timesheet_id || 'n/a'}{' '}
                          stripe {item.stripe_invoice_item_id || 'blank'}
                        </Small>
                      ))}
                  </Stack>
                </Card.Section>
              </Card>
            )}

            {invoiceDetail.stripe_invoice_items.length > 0 && (
              <Card>
                <Card.Section>
                  <Body weight="semibold">Stripe invoice items</Body>
                </Card.Section>
                <Card.Section>
                  <Stack vertical gap={6}>
                    {invoiceDetail.stripe_invoice_items
                      .slice(0, 15)
                      .map((item) => (
                        <Small key={item.id}>
                          {item.id} amount $
                          {((item.amount || 0) / 100).toFixed(2)}{' '}
                          {item.description || 'no description'}
                        </Small>
                      ))}
                  </Stack>
                </Card.Section>
              </Card>
            )}
          </Stack>
        )}
      </div>
    </Modal>
  );
};

type AccountCardProps = {
  account: CycleAccount;
  loadingTimesheetId: number | null;
  loadingCreateAccountId: number | null;
  loadingVoidInvoiceId: number | null;
  isPreviewMode: boolean;
  onAddLineItem: (account: CycleAccount, timesheet: CycleTimesheet) => void;
  onInspectInvoice: (invoiceId: number) => void;
  onCreateInvoice: (account: CycleAccount) => void;
  onVoidInvoice: (account: CycleAccount) => void;
  onReconcile: (account: CycleAccount) => void;
};

const AccountCard: React.FC<AccountCardProps> = ({
  account,
  loadingTimesheetId,
  loadingCreateAccountId,
  loadingVoidInvoiceId,
  isPreviewMode,
  onAddLineItem,
  onInspectInvoice,
  onCreateInvoice,
  onVoidInvoice,
  onReconcile,
}) => {
  const invoiceUrl = account.invoice
    ? `/${ROUTES.invoices}/${account.invoice.id}`
    : null;
  const stripeReference =
    account.invoice?.number || account.invoice?.stripe_invoice_id || null;

  const canVoid =
    account.invoice &&
    account.invoice.status === 'open' &&
    !account.duplicate_invoice;

  return (
    <Card>
      <Card.Section>
        <Stack justify="apart" align="center" wrap gap={16}>
          <Stack vertical gap={6}>
            <Stack gap={8} align="center" wrap>
              <Heading>{account.account_name}</Heading>
              {account.duplicate_invoice && (
                <Chip color="error" label="Duplicate invoice" size="small" />
              )}
            </Stack>
            <Stack gap={10} wrap>
              <div
                style={{
                  padding: '8px 12px',
                  borderRadius: 10,
                  background: '#f8fafc',
                  border: '1px solid #e4e7ec',
                  minWidth: 180,
                }}
              >
                <Small>Customer</Small>
                <Body weight="semibold">{account.customer_name}</Body>
                <Small>ID {account.customer_id}</Small>
              </div>
              <div
                style={{
                  padding: '8px 12px',
                  borderRadius: 10,
                  background: '#f8fafc',
                  border: '1px solid #e4e7ec',
                  minWidth: 160,
                }}
              >
                <Small>Account</Small>
                <Body weight="semibold">{account.account_name}</Body>
                <Small>ID {account.account_id}</Small>
              </div>
            </Stack>
            <Stack gap={8} align="center" wrap>
              {account.invoice ? (
                <>
                  <Stack vertical gap={2}>
                    <Small>
                      Invoice:{' '}
                      <Link to={invoiceUrl!}>#{account.invoice.id}</Link>
                    </Small>
                    {stripeReference && (
                      <Small>Stripe reference: {stripeReference}</Small>
                    )}
                  </Stack>
                  <Chip
                    color="default"
                    label={account.invoice.status}
                    size="small"
                  />
                </>
              ) : (
                <Small>No invoice created yet</Small>
              )}
            </Stack>
          </Stack>

          <Stack gap={8}>
            {!account.invoice && (
              <Button
                a11yLabel={`Create invoice for account ${account.account_id}`}
                appearance="secondary"
                label="Create invoice"
                disabled={isPreviewMode}
                isLoading={loadingCreateAccountId === account.account_id}
                type="button"
                onClick={() => onCreateInvoice(account)}
              />
            )}
            {canVoid && (
              <Button
                a11yLabel={`Void invoice for account ${account.account_id}`}
                appearance="outline"
                label="Void invoice"
                disabled={isPreviewMode}
                isLoading={loadingVoidInvoiceId === account.invoice!.id}
                type="button"
                onClick={() => onVoidInvoice(account)}
              />
            )}
            {account.invoice && (
              <Button
                a11yLabel={`Reconcile invoice for account ${account.account_id}`}
                appearance="secondary"
                label="Reconcile"
                disabled={isPreviewMode}
                type="button"
                onClick={() => onReconcile(account)}
              />
            )}
            <Button
              a11yLabel={`Explore invoice details for account ${account.account_id}`}
              appearance="secondary"
              label="Explore more details"
              disabled={!account.invoice}
              type="button"
              onClick={() =>
                account.invoice && onInspectInvoice(account.invoice.id)
              }
            />
          </Stack>
        </Stack>
      </Card.Section>

      <Card.Section>
        <div style={{ overflowX: 'auto' }}>
          <Table>
            <TableHeader
              fields={[
                { name: 'TIMESHEET', style: { width: 90 } },
                { name: 'WORKER', style: { width: 180 } },
                { name: 'STATUS', style: { width: 180 } },
                { name: 'APPROVED AT', style: { width: 210 } },
                { name: 'TOTAL AMOUNT', style: { width: 140 } },
                { name: 'LINE ITEM', style: { width: 220 } },
                { name: 'IN CURRENT INVOICE', style: { width: 170 } },
                { name: '', style: { width: 160 } },
              ]}
            />
            <tbody>
              {account.timesheets.map((timesheet) => {
                const timesheetUrl =
                  timesheet.order_id != null && timesheet.job_id != null
                    ? `/${ROUTES.orders}/${timesheet.order_id}/jobs/${timesheet.job_id}`
                    : null;
                const actionDisabled =
                  isPreviewMode ||
                  !timesheet.can_add_line_item ||
                  loadingTimesheetId === timesheet.id;
                const actionLabel = isPreviewMode
                  ? 'Preview only'
                  : account.duplicate_invoice
                    ? 'Duplicate invoice'
                    : timesheet.included_in_current_invoice
                      ? 'Included'
                      : timesheet.status === 'linked_to_other_invoice'
                        ? `Linked to #${timesheet.linked_invoice_id}`
                        : timesheet.status === 'requires_review'
                          ? 'Needs review'
                          : 'Add line item';

                return (
                  <TableRow key={timesheet.id}>
                    <TableCell>
                      {timesheetUrl ? (
                        <Link to={timesheetUrl}>
                          <Body weight="semibold">{timesheet.id}</Body>
                        </Link>
                      ) : (
                        <Body weight="semibold">{timesheet.id}</Body>
                      )}
                    </TableCell>
                    <TableCell>
                      <Small>{timesheet.worker_name || 'n/a'}</Small>
                    </TableCell>
                    <TableCell>
                      <Chip
                        color={statusColor(timesheet.status)}
                        label={humanizeStatus(timesheet.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Small>{formatDateTime(timesheet.approved_at)}</Small>
                    </TableCell>
                    <TableCell>
                      <Small>
                        {formatCurrency(timesheet.total_amount_cents)}
                      </Small>
                    </TableCell>
                    <TableCell>
                      <Stack vertical gap={2}>
                        <Small>
                          Local: {timesheet.invoice_line_item_id ?? 'n/a'}
                        </Small>
                        {timesheet.stripe_invoice_item_id && (
                          <Small>
                            Stripe: {timesheet.stripe_invoice_item_id}
                          </Small>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        color={
                          timesheet.included_in_current_invoice
                            ? 'success'
                            : 'warning'
                        }
                        label={
                          timesheet.included_in_current_invoice ? 'Yes' : 'No'
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        a11yLabel={`${actionLabel} for timesheet ${timesheet.id}`}
                        appearance="secondary"
                        label={actionLabel}
                        disabled={actionDisabled}
                        isLoading={loadingTimesheetId === timesheet.id}
                        type="button"
                        onClick={() => onAddLineItem(account, timesheet)}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </tbody>
          </Table>
        </div>
      </Card.Section>
    </Card>
  );
};

const InvoiceHealth = () => {
  const [selectedCycle, setSelectedCycle] = useState<InvoiceCycle | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [accountFilter, setAccountFilter] = useState<AccountFilter>('all');
  const cycles = useCycles();
  const cycleAccounts = useCycleAccounts();
  const addLineItem = useAddLineItem();
  const createInvoice = useCreateInvoice();
  const voidInvoice = useVoidInvoice();
  const reconcile = useReconcile();
  const invoiceDetail = useInvoiceDetail();
  const isPreviewMode = selectedCycle?.is_preview === true;
  const deferredSearchQuery = useDeferredValue(searchQuery);

  const refreshAccounts = useCallback(() => {
    if (!selectedCycle) return;
    cycleAccounts.fetch({
      from: selectedCycle.from,
      to: selectedCycle.to,
      referenceTime: selectedCycle.started_at,
    });
  }, [selectedCycle, cycleAccounts.fetch]);

  useEffect(() => {
    cycles.fetch();
  }, [cycles.fetch]);

  useEffect(() => {
    if (selectedCycle) {
      refreshAccounts();
    }
  }, [selectedCycle, refreshAccounts]);

  const handleCycleSelect = useCallback(
    (cycle: InvoiceCycle) => {
      setSelectedCycle(cycle);
      setSearchQuery('');
      setAccountFilter('all');
      addLineItem.clearError();
      createInvoice.clearError();
      voidInvoice.clearError();
    },
    [addLineItem.clearError, createInvoice.clearError, voidInvoice.clearError],
  );

  const handleAddLineItem = useCallback(
    async (_account: CycleAccount, timesheet: CycleTimesheet) => {
      if (!selectedCycle) return;

      addLineItem.clearError();
      const result = await addLineItem.sync({
        timesheetId: timesheet.id,
        from: selectedCycle.from,
        to: selectedCycle.to,
        referenceTime: selectedCycle.started_at,
      });

      if (result.success) {
        refreshAccounts();
      } else {
        window.alert(result.error || 'Failed to add line item');
      }
    },
    [selectedCycle, addLineItem.sync, addLineItem.clearError, refreshAccounts],
  );

  const handleCreateInvoice = useCallback(
    async (account: CycleAccount) => {
      if (!selectedCycle) return;

      createInvoice.clearError();
      const result = await createInvoice.create({
        accountId: account.account_id,
        referenceTime: selectedCycle.started_at,
      });

      if (result.success) {
        refreshAccounts();
      } else {
        window.alert(result.error || 'Failed to create invoice');
      }
    },
    [
      selectedCycle,
      createInvoice.create,
      createInvoice.clearError,
      refreshAccounts,
    ],
  );

  const handleVoidInvoice = useCallback(
    async (account: CycleAccount) => {
      if (!account.invoice) return;

      const confirmed = window.confirm(
        `Void invoice #${account.invoice.id} for ${account.account_name}? This will void the invoice in Stripe and reset linked timesheets.`,
      );
      if (!confirmed) return;

      voidInvoice.clearError();
      const success = await voidInvoice.voidInvoice(account.invoice.id);

      if (success) {
        refreshAccounts();
      }
    },
    [voidInvoice.voidInvoice, voidInvoice.clearError, refreshAccounts],
  );

  const handleInspectInvoice = useCallback(
    async (invoiceId: number) => {
      const result = await invoiceDetail.inspectInvoice(invoiceId);

      if (!result.success) {
        window.alert(result.error || 'Failed to load invoice details');
        return;
      }

      const detail = result.data;
      if (!detail) return;

      const lines = [
        `Invoice #${detail.invoice.id}`,
        `Status: ${detail.status}`,
        `Stripe ID: ${detail.invoice.stripe_invoice_id || 'n/a'}`,
        '',
        `Local items: ${detail.local_invoice_items.length}`,
        `Stripe items: ${detail.stripe_invoice_items.length}`,
        `Missing locally: ${detail.stripe_items_missing_locally.length}`,
        `Missing in Stripe: ${detail.local_items_missing_in_stripe.length}`,
      ];

      if (detail.issues.length > 0) {
        lines.push('', 'Issues:');
        detail.issues.forEach((issue) =>
          lines.push(`  - ${issue.replace(/_/g, ' ')}`),
        );
      }

      if (detail.net_time_anomalies.length > 0) {
        lines.push(
          '',
          `Net time anomalies: ${detail.net_time_anomalies.length}`,
        );
        detail.net_time_anomalies
          .slice(0, 5)
          .forEach((a) =>
            lines.push(
              `  - Timesheet ${a.timesheet_id}: expected ${a.expected_net_time}min, actual ${a.actual_net_time}min (${a.direction} ${a.difference_minutes}min)`,
            ),
          );
      }

      window.alert(lines.join('\n'));
    },
    [invoiceDetail.inspectInvoice],
  );

  const handleReconcile = useCallback(
    async (account: CycleAccount) => {
      const previewResult = await reconcile.fetchPreview(account.account_id);

      if (!previewResult.success) {
        window.alert(previewResult.error || 'Failed to load reconcile preview');
        return;
      }

      const preview = previewResult.data;
      if (!preview || preview.actions.length === 0) {
        window.alert(
          `Reconcile for ${account.account_name}: No actions needed — ${preview?.invoices_scanned || 0} invoices scanned, everything is in sync.`,
        );
        return;
      }

      const actionSummary = preview.actions
        .map(
          (a) => `- ${a.action.replace(/_/g, ' ')} (Invoice #${a.invoice_id})`,
        )
        .join('\n');

      const confirmed = window.confirm(
        `Reconcile for ${account.account_name}\n\n${preview.invoices_scanned} invoices scanned, ${preview.actions.length} actions found:\n\n${actionSummary}\n\nExecute these actions?`,
      );
      if (!confirmed) return;

      const executeResult = await reconcile.execute(account.account_id);
      if (executeResult.success) {
        window.alert(
          `Reconciliation complete: ${executeResult.data?.actions.length || 0} actions executed.`,
        );
        refreshAccounts();
      } else {
        window.alert(executeResult.error || 'Failed to execute reconciliation');
      }
    },
    [reconcile.fetchPreview, reconcile.execute, refreshAccounts],
  );

  const accounts = useMemo(
    () => cycleAccounts.data?.accounts || [],
    [cycleAccounts.data],
  );
  const filteredAccounts = useMemo(() => {
    const normalizedQuery = deferredSearchQuery.trim().toLowerCase();
    return accounts.filter((account) => {
      const accountName = account.account_name.toLowerCase();
      const customerName = account.customer_name.toLowerCase();
      const accountId = String(account.account_id);
      const matchesSearch =
        accountName.includes(normalizedQuery) ||
        customerName.includes(normalizedQuery) ||
        accountId.includes(normalizedQuery);

      if (!matchesSearch) return false;

      if (accountFilter === 'all') return true;
      if (accountFilter === 'no_invoice') return account.invoice == null;

      const isComplete =
        account.invoice != null &&
        !account.duplicate_invoice &&
        account.missing_timesheets_count === 0;

      if (accountFilter === 'complete') return isComplete;
      return account.invoice != null && !isComplete;
    });
  }, [accounts, accountFilter, deferredSearchQuery]);

  const actionError =
    addLineItem.error || createInvoice.error || voidInvoice.error;

  return (
    <Page title="Invoice Health" size="xl">
      <Stack vertical gap={20}>
        <Card>
          <Card.Section>
            <Stack vertical gap={12}>
              <div>
                <Heading>Invoice Health</Heading>
                <Small>
                  Select a cycle to review approved timesheets by account
                </Small>
              </div>

              {cycles.loading ? (
                <Skeleton.Body lines={2} />
              ) : cycles.error ? (
                <div style={{ color: '#b42318', fontSize: 14 }}>
                  {cycles.error}
                </div>
              ) : (
                <Stack vertical gap={12}>
                  <CycleDropdown
                    cycles={cycles.cycles}
                    selectedCycle={selectedCycle}
                    onSelect={handleCycleSelect}
                  />
                  {selectedCycle && (
                    <Stack gap={12} wrap>
                      <input
                        value={searchQuery}
                        onChange={(event) => setSearchQuery(event.target.value)}
                        placeholder="Search account id, account name, or customer name"
                        style={{
                          width: '100%',
                          maxWidth: 420,
                          padding: '10px 14px',
                          borderRadius: 8,
                          border: '1px solid #d0d5dd',
                          background: '#fff',
                          fontSize: 14,
                          color: '#344054',
                        }}
                      />
                      <select
                        value={accountFilter}
                        onChange={(event) =>
                          setAccountFilter(event.target.value as AccountFilter)
                        }
                        style={{
                          width: '100%',
                          maxWidth: 220,
                          padding: '10px 14px',
                          borderRadius: 8,
                          border: '1px solid #d0d5dd',
                          background: '#fff',
                          fontSize: 14,
                          fontWeight: 500,
                          color: '#344054',
                          cursor: 'pointer',
                          appearance: 'none',
                        }}
                      >
                        <option value="all">All statuses</option>
                        <option value="complete">Complete</option>
                        <option value="incomplete">Incomplete</option>
                        <option value="no_invoice">No invoice</option>
                      </select>
                    </Stack>
                  )}
                </Stack>
              )}
            </Stack>
          </Card.Section>
        </Card>

        {actionError && (
          <Card>
            <Card.Section>
              <div style={{ color: '#b42318', fontSize: 14 }}>
                {actionError}
              </div>
            </Card.Section>
          </Card>
        )}

        {!selectedCycle && !cycles.loading && (
          <Card>
            <Card.Section>
              <EmptyState title="Select a cycle to load accounts and approved timesheets" />
            </Card.Section>
          </Card>
        )}

        {selectedCycle && cycleAccounts.loading && !cycleAccounts.data && (
          <LoadingState text="Loading accounts and timesheets..." />
        )}

        {selectedCycle && cycleAccounts.error && (
          <Card>
            <Card.Section>
              <div style={{ color: '#b42318', fontSize: 14 }}>
                {cycleAccounts.error}
              </div>
            </Card.Section>
          </Card>
        )}

        {selectedCycle && cycleAccounts.data && (
          <>
            {accounts.length === 0 ? (
              <Card>
                <Card.Section>
                  <EmptyState title="No approved timesheets found in this cycle" />
                </Card.Section>
              </Card>
            ) : filteredAccounts.length === 0 ? (
              <Card>
                <Card.Section>
                  <EmptyState title="No accounts match that search" />
                </Card.Section>
              </Card>
            ) : (
              <Stack vertical gap={20}>
                {filteredAccounts.map((account) => (
                  <AccountCard
                    key={account.account_id}
                    account={account}
                    loadingTimesheetId={addLineItem.loadingTimesheetId}
                    loadingCreateAccountId={createInvoice.loadingAccountId}
                    loadingVoidInvoiceId={voidInvoice.loadingInvoiceId}
                    isPreviewMode={isPreviewMode}
                    onAddLineItem={handleAddLineItem}
                    onInspectInvoice={handleInspectInvoice}
                    onCreateInvoice={handleCreateInvoice}
                    onVoidInvoice={handleVoidInvoice}
                    onReconcile={handleReconcile}
                  />
                ))}
              </Stack>
            )}
          </>
        )}
      </Stack>
    </Page>
  );
};

export default InvoiceHealth;
